'use client';

import { PropsWithChildren, createContext, createElement, use, useState } from 'react';

import { Styles, css } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import ChevronDownIcon from '@flipster/ui/assets/svgs/chevron-down.svg';

type HeadingLevel = 2 | 3 | 4 | 5;

const AccordionRootContext = createContext<{ headingLevel?: HeadingLevel }>({});
const AccordionItemContext = createContext<{ isExpanded: boolean; onToggle: () => void } | null>(null);

// TODO feature > accordion 교체
// TODO web > main > accordion 교체
const AccordionRoot = ({
  headingLevel,
  css: cssProps,
  children,
}: PropsWithChildren<{ headingLevel?: HeadingLevel; css?: Styles }>) => {
  return (
    <AccordionRootContext value={{ headingLevel: headingLevel }}>
      <div
        className={css(cssProps, {
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          borderColor: 'border.default.level-1',
        })}
      >
        {children}
      </div>
    </AccordionRootContext>
  );
};

const AccordionItem = ({ css: cssProps, children }: PropsWithChildren<{ css?: Styles }>) => {
  const [isExpanded, setExpanded] = useState<boolean>(false);

  return (
    <AccordionItemContext value={{ isExpanded, onToggle: () => setExpanded((prevState) => !prevState) }}>
      <section
        className={css(
          {
            px: { base: 16, tablet: 24 },
            py: { base: 8, tablet: 24 },
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderColor: 'border.default.level-1',
          },
          cssProps,
        )}
      >
        {children}
      </section>
    </AccordionItemContext>
  );
};

const AccordionHeader = ({
  index,
  onExpanded,
  children,
  css: cssProps,
}: PropsWithChildren<{ index: number; onExpanded?: () => void; css?: Styles }>) => {
  const { headingLevel } = use(AccordionRootContext);
  const itemContext = use(AccordionItemContext);

  return createElement(
    'h' + headingLevel,
    { className: css({ m: 0 }) },
    <button
      type='button'
      aria-expanded={itemContext?.isExpanded}
      className={css(
        hstack.raw({ w: '100%', py: 8, gap: { base: 12, tablet: 20 }, color: 'content.default.level-5' }),
        cssProps,
      )}
      onClick={() => {
        itemContext?.onToggle();
        if (!itemContext?.isExpanded) onExpanded?.();
      }}
    >
      <div
        className={center({
          textStyle: 'Text/M/Strong',
          textAlign: 'center',
          w: 32,
          h: 32,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-1',
          rounded: 4,
        })}
      >
        {index}
      </div>
      <p
        className={css({
          textStyle: 'Title/XS',
          textAlign: 'left',
          flex: 1,
        })}
      >
        {children}
      </p>
      <ChevronDownIcon
        color={token('colors.content.default.level-3')}
        style={{ transform: itemContext?.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
      />
    </button>,
  );
};

const AccordionPanel = ({ css: cssProps, children }: PropsWithChildren<{ css?: Styles }>) => {
  const itemContext = use(AccordionItemContext);

  return (
    <div
      role='region'
      aria-hidden={!itemContext?.isExpanded}
      className={css({ overflow: 'hidden', py: 8 }, cssProps)}
      style={{ display: itemContext?.isExpanded ? 'block' : 'none' }}
    >
      {children}
    </div>
  );
};

export { AccordionRoot as Root, AccordionItem as Item, AccordionHeader as Header, AccordionPanel as Panel };
